import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { LoadingSpinner } from "@newsela/angelou";
import { isEmpty } from "@s-libs/micro-dash";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import { unescapeHTML } from "@/view/utils/htmlEscape";
import pickApplicableWritePrompt from "../utils/pickApplicableWritePrompt";
import useWritePromptPost from "../utils/useWritePromptPost";
import useWritePromptUpdate from "../utils/useWritePromptUpdate";
import AutoGeneratedWritePrompts from "./AutoGeneratedWritePrompts";
import {
  writePromptContainerCss,
  promptHeaderWrapperCss,
  headingCss,
  promptHeadingCss,
} from "./ParentTeacherWritePrompt.style";
import ParentTeacherWritePromptActions from "./ParentTeacherWritePromptActions";
import WritePromptQuestion from "./WritePromptQuestion";
import WritePromptTextInput from "./WritePromptTextInput";

const propTypes = {
  classrooms: PropTypes.arrayOf(PropTypes.object),
  collectionId: PropTypes.number,
  currentArticleLevel: PropTypes.object,
  fetchLegacyWritePrompts: PropTypes.func,
  fetchedWritePrompts: PropTypes.arrayOf(
    PropTypes.shape({
      collections: PropTypes.arrayOf(PropTypes.object),
      id: PropTypes.string,
      teacher: PropTypes.object,
      questions: PropTypes.arrayOf(PropTypes.object),
    }),
  ),
  isParent: PropTypes.bool,
  legacyArticleLevelHeaderId: PropTypes.number,
  legacyArticleLevelId: PropTypes.number,
  legacyWritePromptsLoading: PropTypes.bool,
};

const ParentTeacherWritePrompt = ({
  classrooms,
  collectionId,
  currentArticleLevel,
  fetchLegacyWritePrompts,
  fetchedWritePrompts,
  isParent,
  legacyArticleLevelHeaderId,
  legacyWritePromptsLoading,
}) => {
  const [createWritePrompt, { loading: createWritePromptLoading }] =
    useWritePromptPost();
  const [updateWritePrompt, { loading: updateWritePromptLoading }] =
    useWritePromptUpdate();

  const createOrUpdatedWritePromptAndRefetch = useCallback(
    async (submissionObject) => {
      let result;

      const promptId = submissionObject?.promptId;

      if (promptId) {
        result = await updateWritePrompt(submissionObject);
      } else {
        result = await createWritePrompt(submissionObject);
      }

      await fetchLegacyWritePrompts();
      return result;
    },
    [fetchLegacyWritePrompts, createWritePrompt, updateWritePrompt],
  );

  const {
    query: { copiedAssignmentId },
  } = useRouter();
  const currentWritePrompt = useMemo(
    () =>
      pickApplicableWritePrompt({
        prompts: fetchedWritePrompts,
        collection: { id: collectionId },
        copiedAssignmentId,
      }),
    [fetchedWritePrompts, collectionId],
  );

  const [isEditMode, setIsEditMode] = useState(false);
  const [promptText, setPromptText] = useState("");

  // Return the first question in the array of prompt questions
  const getCurrentQuestion = (prompt) => {
    const question = prompt?.questions?.[0]?.question;
    return unescapeHTML(question);
  };

  useEffect(() => {
    setPromptText(getCurrentQuestion(currentWritePrompt));
  }, [currentWritePrompt]);

  const cancelWritePromptEdit = () => {
    setPromptText(getCurrentQuestion(currentWritePrompt));
    setIsEditMode(false);
  };

  const submitWritePrompt = () => {
    if (
      promptText?.length === 0 ||
      promptText === getCurrentQuestion(currentWritePrompt)
    )
      return;

    const classroomsUniqueIds = classrooms
      .map((classroom) => classroom.unique_id)
      .join(",");

    let submissionObject = {
      collectionId,
      legacyArticleLevelHeaderId,
      text: promptText,
      classroomIds: classroomsUniqueIds,
    };

    const notTeacherOwned = isEmpty(currentWritePrompt?.teacher);

    if (!notTeacherOwned) {
      submissionObject = {
        promptId: currentWritePrompt?.prompt_id,
        text: promptText,
      };
    }

    createOrUpdatedWritePromptAndRefetch(submissionObject);
    setIsEditMode(false);
  };

  const headerRef = useRef();

  const handleAutoGeneratedPromptSelection = (newPromptText) => {
    setPromptText(newPromptText);
    setIsEditMode(true);
    setTimeout(() => {
      headerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 0);
  };

  if (
    createWritePromptLoading ||
    updateWritePromptLoading ||
    legacyWritePromptsLoading
  )
    return <LoadingSpinner />;

  return (
    <div css={writePromptContainerCss}>
      {/* This should be tested when verifying the parent ticket with a valid parent account */}
      {!isParent && (
        <h3 css={headingCss}>
          {isEditMode
            ? "Once this version is saved, the original prompt will be lost."
            : 'Select "Edit" to update the prompt across reading levels. Student responses will appear in the binder.'}
        </h3>
      )}
      <div css={promptHeaderWrapperCss}>
        <h3 css={[headingCss, promptHeadingCss]} ref={headerRef}>
          Prompt
        </h3>
        <ParentTeacherWritePromptActions
          onCancel={cancelWritePromptEdit}
          onSave={submitWritePrompt}
          isEditMode={isEditMode}
          isParent={isParent}
          isTextPresent={promptText?.length > 0}
          onStartEdit={() => setIsEditMode(true)}
        />
      </div>
      {isEditMode ? (
        <WritePromptTextInput
          key={currentWritePrompt?.id}
          handleChange={setPromptText}
          text={promptText}
        />
      ) : (
        <WritePromptQuestion
          writePromptId={currentWritePrompt?.id}
          writePromptQuestion={getCurrentQuestion(currentWritePrompt)}
        />
      )}
      <AutoGeneratedWritePrompts
        onSelectPrompt={handleAutoGeneratedPromptSelection}
        currentArticleLevel={currentArticleLevel}
      />
    </div>
  );
};

ParentTeacherWritePrompt.propTypes = propTypes;

export default ParentTeacherWritePrompt;
